import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Server, ShoppingCart, Receipt, Wallet, LifeBuoy, Bell,
  User as UserIcon, ShieldCheck, Settings, KeyRound, LogOut, Users, HardDrive,
  Cpu, Network, Package, FileText, Ticket, Megaphone, ScrollText, Activity, Sparkles, Boxes, Plug, Rocket, ArrowRightLeft, History, Crown, Mail, Settings2,
} from "lucide-react";
import Logo from "@/components/common/Logo";
import { useAuth, isSuperAdmin } from "@/lib/auth";
import { BRAND } from "@/branding.config";

const CLIENT_NAV = [
  { to: "/portal",                icon: LayoutDashboard, label: "Dashboard",    end: true, tid: "portal-dashboard" },
  { to: "/portal/vps",            icon: Server,          label: "My VPS",       tid: "portal-vps-list" },
  { to: "/portal/deploy",         icon: Rocket,          label: "Deploy VPS",   tid: "portal-deploy" },
  { to: "/portal/orders",         icon: ShoppingCart,    label: "Orders",       tid: "portal-orders" },
  { to: "/portal/invoices",       icon: Receipt,         label: "Invoices",     tid: "portal-invoices" },
  { to: "/portal/transactions",   icon: ArrowRightLeft,  label: "Transactions", tid: "portal-transactions" },
  { to: "/portal/wallet",         icon: Wallet,          label: "Wallet" },
  { to: "/portal/support",        icon: LifeBuoy,        label: "Support",      tid: "portal-tickets" },
  { to: "/portal/notifications",  icon: Bell,            label: "Notifications" },
  { to: "/portal/profile",        icon: UserIcon,        label: "Profile" },
  { to: "/portal/security",       icon: ShieldCheck,     label: "Security" },
  { to: "/portal/api-keys",       icon: KeyRound,        label: "API Keys" },
  { to: "/portal/activity",       icon: History,         label: "Activity Logs" },
  { to: "/portal/settings",       icon: Settings,        label: "Settings" },
];

const ADMIN_NAV = [
  { to: "/admin",                icon: LayoutDashboard, label: "Dashboard",   end: true, tid: "admin-dashboard" },
  { to: "/admin/customers",      icon: Users,           label: "Customers",   tid: "admin-customers" },
  { to: "/admin/roles",          icon: ShieldCheck,     label: "Roles & Perms" },
  { to: "/admin/rbac",           icon: Crown,           label: "Admins & RBAC", superOnly: true, tid: "admin-rbac-nav" },
  { to: "/admin/infrastructure", icon: HardDrive,       label: "Infrastructure", tid: "admin-infrastructure" },
  { to: "/admin/plans",          icon: Package,         label: "VPS Plans",   tid: "admin-plans" },
  { to: "/admin/orders",         icon: ShoppingCart,    label: "Orders",      tid: "admin-orders" },
  { to: "/admin/invoices",       icon: Receipt,         label: "Invoices",    tid: "admin-invoices" },
  { to: "/admin/transactions",   icon: ArrowRightLeft,  label: "Transactions", tid: "admin-transactions" },
  { to: "/admin/wallet",         icon: Wallet,          label: "Wallet",      tid: "admin-wallet" },
  { to: "/admin/payments",       icon: Wallet,          label: "Payments" },
  { to: "/admin/coupons",        icon: Sparkles,        label: "Coupons" },
  { to: "/admin/tickets",        icon: Ticket,          label: "Tickets",     tid: "admin-tickets" },
  { to: "/admin/announcements",  icon: Megaphone,       label: "Announcements" },
  { to: "/admin/system-logs",    icon: FileText,        label: "System Logs" },
  { to: "/admin/audit-logs",     icon: ScrollText,      label: "Audit Logs" },
  { to: "/admin/monitoring",     icon: Activity,        label: "Monitoring" },
  { to: "/admin/integrations",   icon: Plug,            label: "Integrations", tid: "admin-integrations" },
  { to: "/admin/email",          icon: Mail,            label: "Email Templates", tid: "admin-email" },
  { to: "/admin/system",         icon: Settings2,       label: "Configuration",   tid: "admin-config" },
  { to: "/admin/profile",        icon: UserIcon,        label: "My Profile",  tid: "admin-profile" },
  { to: "/admin/settings",       icon: Settings,        label: "Settings",    tid: "admin-settings" },
];

export default function PortalLayout({ variant }) {
  const isAdmin = variant === "admin";
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const rawNav = isAdmin ? ADMIN_NAV : CLIENT_NAV;
  const nav = rawNav.filter((n) => !n.superOnly || isSuperAdmin(user?.role));
  const label = isAdmin ? BRAND.product_labels.admin : BRAND.product_labels.portal;

  const handleLogout = async () => { await logout(); navigate(isAdmin ? "/admin/login" : "/login"); };

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        data-testid={isAdmin ? "admin-sidebar" : "portal-sidebar"}
        className="w-64 shrink-0 border-r border-white/[0.06] sticky top-0 h-screen flex flex-col bg-[#0A0614]/60 backdrop-blur-xl"
      >
        <div className="px-5 h-16 flex items-center border-b border-white/[0.06]">
          <Logo />
        </div>
        <div className="px-4 pt-4 pb-2 text-[10px] uppercase tracking-[0.24em] text-muted-foreground/70 font-bold">
          {label}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.tid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-2xl px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary/12 text-foreground border border-primary/25"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/[0.03] border border-transparent"
                  }`
                }
              >
                <Icon size={15} className="opacity-90" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-white/[0.06] p-3 space-y-2.5">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            {user?.picture ? (
              <img
                src={user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL || ""}${user.picture}`}
                alt=""
                className="h-8 w-8 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ background: `linear-gradient(135deg, ${BRAND.primary_color}, ${BRAND.accent_color})` }}
              >
                {(user?.name || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="portal-logout-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs hover:bg-white/[0.04] transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="h-16 flex items-center justify-between px-8 border-b border-white/[0.06] bg-background/50 backdrop-blur">
          <div className="text-xs text-muted-foreground font-mono truncate">{location.pathname}</div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-3 py-1 rounded-full border border-white/10 text-muted-foreground">
              {isAdmin ? "Admin" : "Customer"} · {user?.role}
            </span>
          </div>
        </div>
        <div className="p-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
